import { useState, useEffect } from "react";
import { differenceInDays, format } from "date-fns";
import { signal, useSignal } from "@preact/signals-react";

import GridCards from "componentsv2/card/GridCards";
import BasicTable from "componentsv2/table/BasicTable";
import DatePicker from "componentsv2/form/DatePicker";
import Integer from "componentsv2/form/Integer";
import Dropdown from "componentsv2/form/Dropdown";
import Button from "componentsv2/button/Button";
import { layout as modelViewRooms } from "./models/modelWizardRooms";
import { layout as modelFolioWizard } from "./models/modelFolioWizard";
import { computeReverseTax, getProductTax } from "./helper";
import { fetchAvailability } from "apps/Booking/availability";
import { getStorage } from "store/local";
import CardFolio from "./CardFolio";
import proxy from "api/proxy";
// import ProductPackage from "./ProductPackage";
import { genStoreId } from "componentsv2/tools/functions";

var sequenceId = 0;
const msgError = signal(null);
const configHotel = signal(null);
const layoutFolioWizard = modelFolioWizard();

function getRandomRoom(rooms) {
  const roomRand = Math.floor(Math.random() * rooms.length);
  const foundRooms = rooms.splice(roomRand, 1);
  if (foundRooms.length > 0) {
    return foundRooms[0];
  }
}

const lastSelected = signal(null);
const quantityPackage = signal(0);

function WizardAddRoom({
  onClose,
  parentRec,
  reload,
  record: recordForm,
  signalReset,
}) {
  const [records, setRecords] = useState(null);
  const [roomsSelected, setRoomsSelected] = useState(new Map());
  const [layout, setLayout] = useState(null);
  const nights = useSignal(0);
  const arrival = useSignal("");
  const departure = useSignal("");
  const selectedRooms = useSignal(new Map());
  const priceList = useSignal({});
  const storeId = genStoreId("hotel.booking.add_room", null, null);

  function resetWizard() {
    setRecords(null);
    lastSelected.value = null;
    quantityPackage.value = 0;
    msgError.value = null;
    nights.value = 0;
    priceList.value = {
      id: parentRec.price_list.id,
      name: parentRec.price_list.rec_name,
    };
    arrival.value = "";
    departure.value = "";
    selectedRooms.value = new Map();
    setRoomsSelected(new Map());
  }

  function handleDelete(record) {
    const _roomsSelected = new Map(roomsSelected);
    _roomsSelected.delete(record.room.id);
    setRoomsSelected(_roomsSelected);
    const _records = new Map(records);
    const category = _records.get(record.product.id);
    category.rooms.push(record.room);
    category.available = category.rooms.length;
  }

  async function getLayout() {
    const config = await getConfig();
    configHotel.value = config;
    const layout = await modelViewRooms(config, addRoom);
    setLayout(layout);
  }

  useEffect(() => {
    getLayout();
    resetWizard();
  }, [signalReset]);

  async function saveRooms(newFolios) {
    for (const folio of newFolios) {
      folio.booking = parentRec.id;
      await proxy.saveQuery({
        model: "hotel.folio",
        storeRec: folio,
      });
    }
  }

  async function handleAccept() {
    const records = Array.from(selectedRooms.value.values());
    for (const rec of records) {
      sequenceId -= 1;
      delete rec.total_amount;
      rec.id = sequenceId;
      rec.registration_state = "pending";
    }

    await saveRooms(records);
    resetWizard();
    if (reload) {
      await reload();
    }
    onClose();
  }

  async function getProducts(acco, nights) {
    const model = "product.product";
    const domain = [
      ["id", "=", acco.id],
      ["products_mix", "!=", null],
      ["products_mix.option.template.salable", "=", true],
    ];
    const fieldsNames = ["products_mix.option.name", "products_mix.list_price"];
    const { data } = await proxy.search(model, domain, fieldsNames);
    const products = [];
    for (const product of data) {
      for (const pmix of product["products_mix."]) {
        products.push({
          product: pmix["option."].id,
          description: pmix["option."].name,
          unit_price: pmix.list_price,
          quantity: nights,
          status: "draft",
          sale_date: format(new Date(), "yyyy-MM-dd"),
        });
      }
    }
    return products;
  }

  function handleAddOption(charge) {
    const target = roomsSelected.get(lastSelected.value);
    const unit_price = charge.unit_price;
    charge.unit_price = charge.unit_price.toString();
    target.charges = [["create", [charge]]];
    quantityPackage.value += 1;
    // FIXME Taxes?
    target.total_amount =
      parseFloat(target.total_amount) + Number(unit_price) * 1.19;
  }

  async function getConfig() {
    const model = "hotel.configuration";
    const session = await getStorage("ctxSession");
    const fieldsNames = ["company", "sale_price_taxed_policy"];
    const domain = [["company", "=", session.company]];
    const { data, error } = await proxy.search(model, domain, fieldsNames);
    if (error) {
      console.error(error);
      return {};
    }
    return data[0];
  }

  async function addRoom(accom) {
    const _roomsSelected = selectedRooms.value;
    let room;
    if (accom.room.id) {
      room = accom.room;
    } else {
      room = getRandomRoom(accom.rooms);
    }
    accom.available = accom.rooms.length;

    let unit_price = accom.unit_price;
    let unit_price_taxed = accom.unit_price_w_tax;
    if (!room) {
      return;
    }

    if (configHotel.value.sale_price_taxed_policy) {
      unit_price = computeReverseTax(accom.accommodation.id, unit_price_taxed);
    } else {
      unit_price_taxed = computePriceTaxed(accom.accommodation.id, unit_price);
    }

    if (accom.occupancy && accom.occupancy[0].unit_price !== unit_price) {
      for (const occ of accom.occupancy) {
        occ.unit_price = unit_price;
      }
    }

    const total_amount = nights.value * unit_price_taxed;
    const newRoom = {
      room: room,
      product: accom.accommodation,
      arrival_date: arrival.value,
      departure_date: departure.value,
      unit_price: unit_price,
      unit_price_w_tax: unit_price_taxed.toString(),
      num_adults: accom.adults || 1,
      num_children: accom.children || 0,
      pax: accom.adults + accom.children,
      nights_quantity: nights.value,
      total_amount: total_amount.toFixed(2),
      occupancy: [["create", accom.occupancy]],
    };
    _roomsSelected.set(room.id, newRoom);
    lastSelected.value = room.id;
    selectedRooms.value = new Map(_roomsSelected);
    const products = await getProducts(accom.accommodation, nights.value);
    // setProductsPackage(products);
  }

  function computePriceTaxed(productId, unitPrice) {
    return (unitPrice * (1 + getProductTax(productId))).toFixed(2);
  }

  function triggerRowUpdate(record, field, value) {
    if (field.name === "unit_price") {
      record.value.unit_price = value;
      record.value.unit_price_w_tax = computePriceTaxed(
        record.value.accommodation.id,
        value,
      );
    }
  }

  async function withChange(fieldName, value) {
    let _nights = 0;

    if (arrival.value && departure.value) {
      _nights = differenceInDays(departure.value, arrival.value);
      nights.value = _nights;
      nights.value = _nights;
      await getAvailabilityPrices();
    }
  }

  const fieldArrival = {
    level: "wizard",
    name: "arrival_date",
    label: "hotel.booking.add_room.arrival_date",
    onChange: withChange,
  };

  const fieldDeparture = {
    level: "wizard",
    name: "departure_date",
    label: "hotel.booking.add_room.departure_date",
    onChange: withChange,
  };

  const fieldNights = {
    label: "hotel.booking.add_room.nights_quantity",
    name: "nights_quantity",
    readOnly: true,
  };

  const fieldPriceList = {
    label: "hotel.booking.price_list",
    name: "price_list",
    readOnly: true,
    recSearch: () => [],
  };

  async function getAvailabilityPrices() {
    msgError.value = null;
    const priceListId = parentRec.price_list.id;
    const data = await fetchAvailability({
      parentRec,
      startDate: arrival.value,
      endDate: departure.value,
      priceListId,
    });

    const mapRecs = new Map();
    // FIXME add tax value in backend
    if (data?.status === "error") {
      if (data.message === "minimum_stay_not_valid") {
        msgError.value = `La estadía mínima para la fecha es de ${data.minimum_stay} noches`;
      }
      return;
    }
    if (!data?.records) {
      return;
    }
    data.records.forEach((value) => {
      const occupancy = [];
      let unit_price = 0;
      for (const pl of value.price_lists) {
        if (priceListId !== pl.price_list) continue;
        for (const [date, price] of Object.entries(pl.prices_by_date)) {
          unit_price = price;
          occupancy.push({
            occupancy_date: date,
            unit_price: price.toFixed(2),
            state: "draft",
          });
        }
        break;
      }
      value.accommodation = { id: value.id, name: value.name };
      value.adults = value.max_adults || 1;
      value.children = value.max_children || 0;
      value.unit_price = unit_price.toFixed(2);
      value.sale_price_taxed = computePriceTaxed(value.id, unit_price);
      value.room = {};
      value.occupancy = occupancy;
      mapRecs.set(value.id, value);
    });

    setRecords(mapRecs);
  }

  function getCardsData() {
    return [...selectedRooms.value.values()];
  }

  return (
    <div id="hotel-booking-wizard-rooms" className="block md:min-h-100">
      {msgError.value && (
        <span className="text-lg text-rose-700">{msgError.value}</span>
      )}
      <div className="grid grid-cols-4 gap-x-2">
        <DatePicker {...fieldArrival} data={arrival} />
        <DatePicker {...fieldDeparture} data={departure} />
        <Integer {...fieldNights} data={nights} />
        <Dropdown {...fieldPriceList} data={priceList} />
      </div>
      <div className="mt-6">
        {records && records.size > 0 && (
          <BasicTable
            storeId={storeId}
            layout={layout}
            records={records}
            triggerUpdate={triggerRowUpdate}
            isLoading={false}
            limit={30}
          />
        )}
      </div>
      {/* {productsPackage && (
        <div className="flex">
          <span className="font-bold text-xl text-neutral-700 my-auto mr-5">
            Deseas agregar este servicio?
          </span>
          <ProductPackage
            records={productsPackage}
            handleAdd={handleAddOption}
            quantity={quantityPackage.value}
          />
        </div>
      )}*/}
      {selectedRooms.value.size > 0 && (
        <GridCards
          id="add-room-grid-cards"
          records={getCardsData()}
          handleDelete={handleDelete}
          layout={layoutFolioWizard}
          Card={CardFolio}
        />
      )}
      <div className="mt-6 w-full h-full space-x-2 flex justify-end sticky bottom-0 px-4 rounded-md">
        <Button
          style="font-semibold"
          color="rose"
          loading={false}
          onClick={onClose}
          content="hotel.folio.guest.cancel"
        />
        {selectedRooms.value.size > 0 && (
          <Button
            style="font-semibold"
            color="amber"
            onClick={handleAccept}
            content="hotel.booking.add_rooms.accept"
          />
        )}
      </div>
    </div>
  );
}

export default WizardAddRoom;
